import styled from 'styled-components';
import { motion, useScroll, useTransform } from 'framer-motion';
import { FaWhatsapp } from 'react-icons/fa';
import { useRef } from 'react';

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12 } },
};
const item = {
  hidden: { opacity: 0, y: 40 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
};

export default function Hero() {
  const sectionRef = useRef(null);

  // Scroll progress dentro de la sección
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start start', 'end start'],
  });

  // La imagen se desplaza hacia la derecha conforme baja el scroll
  const imgX = useTransform(scrollYProgress, [0, 1], ['0%', '60%']);
  const imgOpacity = useTransform(scrollYProgress, [0, 0.6], [1, 0]);

  return (
    <Section id="hero" ref={sectionRef}>
      <Grid />
      <Glow />

      <Inner>
        {/* Columna izquierda: textos */}
        <Content
          as={motion.div}
          variants={container}
          initial="hidden"
          animate="show"
        >
          <Eyebrow as={motion.span} variants={item}>
            Fabricación Industrial de Tarimas
          </Eyebrow>

          <Title as={motion.h1} variants={item}>
            <TitleLine>SOMOS LÍDERES</TitleLine>
            <TitleLine $accent>EN TARIMAS</TitleLine>
            <TitleLine>DE MADERA</TitleLine>
          </Title>

          <Subtitle as={motion.p} variants={item}>
            Soluciones en madera nueva, reciclada y mixta adaptadas a los
            requerimientos específicos de cada cliente, con estándares de
            calidad e higiene industrial.
          </Subtitle>

          <Actions as={motion.div} variants={item}>
            <WABtn href="https://wa.me/524311011283?text=Hola%2C%20los%20contacto%20desde%20su%20sitio%20web." target="_blank" rel="noopener">
              <FaWhatsapp size={20} />
              Solicitar cotización
            </WABtn>
            <SecondaryBtn onClick={() => document.querySelector('#products')?.scrollIntoView({ behavior: 'smooth' })}>
              Ver productos
            </SecondaryBtn>
          </Actions>

          <Stats as={motion.div} variants={item}>
            <Stat><strong>+20</strong><span>Años de experiencia</span></Stat>
            <StatDivider />
            <Stat><strong>100%</strong><span>Calidad garantizada</span></Stat>
            <StatDivider />
            <Stat><strong>3</strong><span>Líneas de producto</span></Stat>
          </Stats>
        </Content>

        {/* Columna derecha: imagen con animación de scroll */}
        <ImageCol
          style={{ x: imgX, opacity: imgOpacity }}
          as={motion.div}
          initial={{ opacity: 0, x: 60 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 1, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
        >
          <HeroPallet
            src="/img/hero/pngtree-new-wooden-platforms-pallet-png-image_10050183.png"
            alt="Tarima de madera Grupo Jaycom"
          />
        </ImageCol>      </Inner>

      <ScrollHint
        as={motion.div}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
      >
        <ScrollLine
          as={motion.div}
          animate={{ scaleY: [0, 1, 0] }}
          transition={{ repeat: Infinity, duration: 1.6, ease: 'easeInOut' }}
        />
        <span>ver más</span>
      </ScrollHint>
    </Section>
  );
}

const Section = styled.section`
  position: relative;
  min-height: 100dvh;
  display: flex;
  align-items: center;
  overflow: hidden;
  background: transparent;
`;

const Inner = styled.div`
  position: relative;
  z-index: 1;
  max-width: 1280px;
  margin: 0 auto;
  padding: 8rem 2rem 5rem;
  width: 100%;
  display: grid;
  grid-template-columns: 1fr 1fr;
  align-items: center;
  gap: 2rem;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const Content = styled.div`
  position: relative;
  z-index: 2;

  @media (max-width: 900px) {
    grid-column: 1 / -1;
  }
`;

const ImageCol = styled(motion.div)`
  display: flex;
  align-items: center;
  justify-content: center;
  will-change: transform, opacity;

  /* En móvil: posición absoluta detrás del texto */
  @media (max-width: 900px) {
    position: absolute;
    inset: 0;
    z-index: 1;
    pointer-events: none;
    align-items: center;
    justify-content: flex-end;
    padding-right: 0;
    overflow: hidden;
  }
`;

const HeroPallet = styled.img`
  width: 100%;
  max-width: 720px;
  filter: drop-shadow(0 30px 60px rgba(0,0,0,0.15));
  user-select: none;
  pointer-events: none;

  /* En móvil: más opaca para no tapar el texto, y más pequeña */
  @media (max-width: 900px) {
    max-width: 380px;
    opacity: 0.18;
    filter: drop-shadow(0 10px 30px rgba(0,0,0,0.1));
  }
`;

const Grid = styled.div`
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(rgba(31,93,140,0.05) 1px, transparent 1px),
    linear-gradient(90deg, rgba(31,93,140,0.05) 1px, transparent 1px);
  background-size: 60px 60px;
  mask-image: radial-gradient(ellipse 80% 80% at 50% 50%, black 30%, transparent 100%);
`;

const Glow = styled.div`
  position: absolute;
  top: -10%;
  right: -5%;
  width: 600px;
  height: 600px;
  background: radial-gradient(ellipse, rgba(31,93,140,0.07) 0%, transparent 70%);
  pointer-events: none;
`;

const Eyebrow = styled.span`
  display: inline-flex;
  align-items: center;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: var(--blue);
  border-left: 3px solid var(--red);
  padding: 0.2rem 0 0.2rem 0.75rem;
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  font-family: 'Bebas Neue', sans-serif;
  font-size: clamp(3.5rem, 10vw, 9rem);
  line-height: 0.95;
  letter-spacing: -0.02em;
  margin-bottom: 1.75rem;
  color: var(--text);
`;

const TitleLine = styled.span`
  display: block;
  color: ${({ $accent }) => $accent ? 'var(--red)' : 'var(--blue)'};
  text-shadow: ${({ $accent }) => $accent ? 'var(--shadow-3d)' : 'none'};
`;

const Subtitle = styled.p`
  max-width: 540px;
  font-size: clamp(1rem, 2vw, 1.15rem);
  line-height: 1.7;
  color: var(--text-sub);
  margin-bottom: 2.5rem;
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 1.25rem;
  flex-wrap: wrap;
  margin-bottom: 4rem;
`;

const WABtn = styled.a`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  background: var(--green-wa);
  color: #fff;
  padding: 0.9rem 2rem;
  border-radius: 0;
  font-weight: 700;
  font-size: 0.95rem;
  letter-spacing: 0.04em;
  transition: transform 0.2s, box-shadow 0.2s;
  box-shadow: 0 8px 30px rgba(37,211,102,0.2);
  @media (hover: hover) {
    &:hover { transform: translateY(-2px); box-shadow: 0 14px 40px rgba(37,211,102,0.3); }
  }
`;

const SecondaryBtn = styled.button`
  background: none;
  border: 2px solid var(--blue);
  color: var(--blue);
  padding: 0.9rem 2rem;
  border-radius: 0;
  font-weight: 700;
  font-size: 0.95rem;
  cursor: pointer;
  letter-spacing: 0.04em;
  transition: background 0.2s, color 0.2s;
  @media (hover: hover) {
    &:hover { background: var(--blue); color: #fff; }
  }
`;

const Stats = styled.div`
  display: flex;
  align-items: center;
  gap: 2rem;
  flex-wrap: wrap;
`;

const Stat = styled.div`
  display: flex;
  flex-direction: column;
  strong {
    font-family: 'Bebas Neue', sans-serif;
    font-size: 2.5rem;
    color: var(--red);
    line-height: 1;
    text-shadow: var(--shadow-3d);
  }
  span {
    font-size: 0.78rem;
    color: var(--text-sub);
    letter-spacing: 0.06em;
    text-transform: uppercase;
    margin-top: 0.2rem;
  }
`;

const StatDivider = styled.div`
  width: 1px;
  height: 40px;
  background: var(--border);
`;

const ScrollHint = styled.div`
  position: absolute;
  bottom: 2.5rem;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  span {
    font-size: 0.65rem;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    color: rgba(0,0,0,0.25);
  }
`;

const ScrollLine = styled.div`
  width: 1px;
  height: 40px;
  background: var(--red);
  transform-origin: top;
`;
